import type { Marinade } from "@marinade.finance/marinade-ts-sdk";
import type { MarinadeReferralStateResponse } from "@marinade.finance/marinade-ts-sdk/dist/src/marinade-referral-state/marinade-referral-state.types";
import { useConnection } from "@solana/wallet-adapter-react";
import { StakeProgram, PublicKey } from "@solana/web3.js";
import { useSearchParams } from "next/navigation";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { coinSymbols } from "services/domain/coinSymbols";
import {
  getMarinade,
  getMarinadeReferralPartnerState,
} from "services/marinade/sdk";
import { MARINADE_REFERRAL } from "solana/ids";
import { isBase58 } from "utils/is-base58";

import { useWalletInfo } from "./WalletInfoContext";

const MarinadeContext = createContext<{
  marinade?: Marinade;
  referralCode?: string;
  referralState?: MarinadeReferralStateResponse.ReferralState;
  stakeAccountRent: number;
  hasMSolAta: boolean;
}>({
  marinade: undefined,
  referralCode: undefined,
  referralState: undefined,
  stakeAccountRent: 0,
  hasMSolAta: false,
});

export const MarinadeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { connection } = useConnection();
  const [referralState, setReferralState] =
    useState<MarinadeReferralStateResponse.ReferralState>();
  const [stakeAccountRent, setStakeAccountRent] = useState<number>(0);
  const [hasMSolAta, setHasMSolAta] = useState(false);
  const [marinade, setMarinade] = useState<Marinade>();

  const { tokenAccounts, walletAddress } = useWalletInfo();

  const searchParams = useSearchParams();
  const referralCode = searchParams?.get("referralCode") ?? "";

  useEffect(() => {
    const fetchRent = async () => {
      const stakeAccRent = await connection.getMinimumBalanceForRentExemption(
        StakeProgram.space
      );
      setStakeAccountRent(stakeAccRent);
    };
    fetchRent();
  }, [connection]);

  useEffect(() => {
    if (!walletAddress) return;

    const refCode =
      referralCode && isBase58(referralCode)
        ? new PublicKey(referralCode)
        : MARINADE_REFERRAL;
    setMarinade(getMarinade(connection, walletAddress, refCode));
  }, [walletAddress, connection, referralCode]);

  useEffect(() => {
    const checkMsolAta = async () => {
      const mSOLtokenAcc = tokenAccounts.get(coinSymbols.mSOL);
      if (mSOLtokenAcc?.address) {
        const accInfo = await connection.getAccountInfo(
          mSOLtokenAcc?.address,
          "confirmed"
        );
        setHasMSolAta(!!accInfo);
      }
    };
    checkMsolAta();
  }, [connection, tokenAccounts]);

  useEffect(() => {
    const fetchState = async () => {
      const state = await getMarinadeReferralPartnerState(
        connection,
        referralCode ? new PublicKey(referralCode) : MARINADE_REFERRAL
      );
      setReferralState(state);
    };
    fetchState();
  }, [referralCode, connection]);

  const value = useMemo(
    () => ({
      marinade,
      referralCode,
      referralState,
      stakeAccountRent,
      hasMSolAta,
    }),
    [marinade, hasMSolAta, referralCode, referralState, stakeAccountRent]
  );

  return (
    <MarinadeContext.Provider value={value}>
      {children}
    </MarinadeContext.Provider>
  );
};

export function useMarinade() {
  return useContext(MarinadeContext);
}
